import { DataTable } from "@/components/datatable/datatable"
import { DataTableSupabase } from "@/components/datatable/supadatatable"
import { Page, PageTitle } from "@/components/page"
import { usePage } from "@/components/page/hooks"
import { Button } from "@/components/ui/button"
import { supabase } from "@/lib/data"
import { useTeamStore } from "@/lib/hooks/useTeam"
import { Route, Plus, Trash2, Copy } from "lucide-react"
import { useCallback } from "react"
import { ROUTER_WEBHOOK } from "@/pages/dev/channels/webhooks"
import { useAssistantChangeKey } from "@/lib/assistant/modules/channels"

export const routers = {
  title: "Router",
  description: "Route incoming Meta webhooks to a channel by page, Instagram account or WhatsApp phone number.",
  route: "/settings/routers",
  url: "/settings/routers",
  icon: Route,
  component,
  group: "communications",
  resource: "apikeys",
  action: "read",
} satisfies PageConfig

const types = {
  whatsapp: "WhatsApp",
  messenger: "Facebook Messenger",
  instagram: "Instagram",
} as const

// The platform is not stored on the row: it comes from the channel, so it is shown next to its title.
const columns: Columns<Router> = {
  title: { header: "Title", size: 200 },
  asset_id: { header: "Asset ID", size: 200 },
  channel: {
    header: "Channel", size: 280,
    cell: ({ row }) => {
      const channel = row.original.channel as any
      return channel ? `${channel.title} (${types[channel.type] || channel.type})` : ""
    },
  },
  created_at: { header: "Created", cell: DataTable.cellRender.date, size: 166 },
}

// A single url for every row: Meta configures the callback once per app, not per asset.
const QAWebhook = ROUTER_WEBHOOK.qa
const PRODWebhook = ROUTER_WEBHOOK.prod

function component() {

  const { selectedTeam } = useTeamStore()
  const { navigate } = usePage()
  const filter = useCallback(q => q.eq("team", selectedTeam?.id), [selectedTeam])
  // The assistant changes routers from any page; reload the list when it does
  const tableKey = useAssistantChangeKey()

  const menu: DropdownMenuContents = [
    {
      title: "Delete", action: async (v) => {
        await supabase.from("routers").delete().eq("id", v.id)
      },
      icon: <Trash2 />,
      ask: "Are you sure you want to delete this router?",
    },
  ]

  return <Page>
    <div className="h-full grid grid-rows-[auto_1fr] gap-4">
      <div className="flex">
        <PageTitle />
        <div className="grow"></div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-lg" onClick={() => navigator.clipboard.writeText(QAWebhook)}><Copy className="h-4 w-4" />QA Webhook</Button>
          <Button variant="outline" className="rounded-lg" onClick={() => navigator.clipboard.writeText(PRODWebhook)}><Copy className="h-4 w-4" />Prod Webhook</Button>
          <Button className="rounded-lg" onClick={() => navigate('/settings/routers/new')}><Plus className="h-4 w-4" />New Router</Button>
        </div>
      </div>
      <DataTableSupabase
        key={tableKey}
        table="routers"
        columns={columns}
        showSelection
        onRowClick={"/settings/routers"}
        sort={["created_at", "desc"]}
        actions={menu}
        filter={filter}
        enableServerSideFiltering={true}
        select={`
        *,
        channel (
          title,
          type
        )
      `}
        searchableColumns={["title", "asset_id"]}
      />
    </div>
  </Page>

}
